import {LitElement, html, css} from 'lit';
import {connect} from 'pwa-helpers';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {property} from 'lit/decorators/property.js';
import SunCalc from 'suncalc';
import {getFuzzyLocalTimeFromPoint} from '@mapbox/timespace';
import {msg} from '@lit/localize';
import store from '../../../store';
import {lightProfiles} from '../../lightProfiles';
import {selectColorTemperature, selectProfileId, selectSpcConfiguration} from '../../../selectors';
import {setChannelId} from '../../lightProfileSlice';
import {pimText} from '../../../util/values';

@customElement('param-table')
export class ParamTable extends connect(store)(LitElement) {

	static styles = [
		css`
			.paramTable {
				border-collapse: collapse;
				margin: 3cqw 0 0 0;
				width: 100%;
			}

			.paramTable th {
				font: var(--font-specsheet-print-bold);
				font-size: 1cqw;
				padding: 0.5cqw 1cqw 0.5cqw 0;
				text-align: left;
				vertical-align: top;
				width: 25%;
			}


			.paramTable tr {
				border-bottom: 1px solid #C6C6C6;
				padding-left: 0;
				vertical-align: top;
			}

			.paramTable td {
				font: var(--font-specsheet-print-light);
				font-size: 1cqw;
				padding: 5px 10px 5px 0;
				width: 25%;
			}

			.date {
				font: var(--font-specsheet-print-bold);
				font-size: 1cqw;

			}
			.sun {
				font-size: 0.75cqw;
			}

			.label {
				font: var(--font-specsheet-print-bold);
				font-size: 1cqw;
			}

			.value {
				font: var(--font-specsheet-print-light);
				font-size: 1cqw;
			}
		`];

	@property({type: String})
	date = '';

	renderChannels = [];

	constructor(props) {
		super(props);
		store.subscribe(() => this.requestUpdate());

		// creating table rows based on different examplary times
		this.rows = [
			{time: '00:00:00'},
			{time: '04:00:00'},
			{time: '08:00:00'},
			{time: '12:00:00'},
			{time: '16:00:00'},
			{time: '20:00:00'}
		];
	}

	stateChanged(state) {
		this.state = state;
		this.translations = state.productData.translations;
		this.profile = state.lightProfile.data.profile;
		this.lightProfile = state.lightProfile.data;
		this.location = state.lightProfile.data.project.location;
		this.lang = state.status.lang;
		this.userProfile = state.userProfile.data;
		this.spcConfiguration = selectSpcConfiguration(state);

		const profileId = selectProfileId(state);
		if (profileId) {
			const lightProfile = lightProfiles.find(lp => lp.id === profileId);
			this.renderChannels = lightProfile?.renderChannels;
			this.layers = lightProfile?.layers;
		}

		this.fixedDate = new Date(this.date);
	}

	// convert "10:00 AM" to number of seconds of day to use in calculations
	getTimeInSeconds(time) {
		const timeParts = time.split(':');
		const hours = parseInt(timeParts[0], 10);
		const minutes = parseInt(timeParts[1], 10);
		return hours * 3600 + minutes * 60;
	}

	// getKeyframe with fixed day-dayOfYear and timeInSeconds instead of dynamic values on the interactive configurator - selectedDay and selectedSecond
	getKeyframe(keyframes, dayOfYear, timeInSeconds) {
		const timezone = getFuzzyLocalTimeFromPoint(Date.now(), [this.location.longitude, this.location.latitude]);
		const myOffset = new Date().getTimezoneOffset();
		const offset = timezone._offset + myOffset;

		const date = new Date(2024, 0, dayOfYear, 0, 0, timeInSeconds - offset * 60);
		const localdatestring = date.toLocaleString('en-US', {dateStyle: 'medium', timeStyle: 'medium'});
		const locald = new Date(Date.parse(localdatestring));
		const timestamp = locald.getTime();
		const keyframe = keyframes.find(kf => kf[0] * 1000 > timestamp && kf[0] * 1000 < timestamp + 3600 * 12 * 1000 && kf[0] * 1000 > timestamp - 3600 * 12 * 1000);
		// find the element before keyframe in the array
		const index = keyframes.indexOf(keyframe);
		return keyframes[index - 1];
	}

	// calculating intensity values for each render channel with fixed values, channels are depending on the profile chosen, there can be 2 or 1 channel, each can contain 1 or 2 layers
	getIntensityValue(renderChannel, dayOfYear, timeInSeconds) {
		//console.log( 'getIntensityValue:', renderChannel, dayOfYear, timeInSeconds);
		let displayIntensity = 0;
		if (renderChannel) {
			renderChannel.layers.forEach(layer => {
				const keyframe = this.getKeyframe(this.lightProfile.simulationResult.targets[layer].keyframes, dayOfYear, timeInSeconds);
				const inten = keyframe ? (keyframe[2] * 100) : 0;
				displayIntensity = Math.max(inten, displayIntensity);
			});
		}
		return displayIntensity;
	}

	// calculating temperature values for each render channel with fixed values, channels are depending on the profile chosen, there can be 2 or 1 channel, each can contain 1 or 2 layers
	getTemperatureValue(renderChannel, dayOfYear, timeInSeconds) {
		//console.log( 'getTemperatureValue:', renderChannel, dayOfYear, timeInSeconds);
		let displayTemperature = '---';
		let displayIntensity = 0;
		if (renderChannel) {
			renderChannel.layers.forEach(layer => {
				const keyframe = this.getKeyframe(this.lightProfile.simulationResult.targets[layer].keyframes, dayOfYear, timeInSeconds);
				let ctt;
				if (keyframe && keyframe.length === 4) {
					ctt = this.getTemperature(keyframe[3]);
				} else {
					if (this.lightProfile.project.base === 'AdjustingWhite' ||
						this.lightProfile.project.base === 'DynamicAmbience' ||
						this.lightProfile.project.base === 'DynamicPath') {
						const colorTemperatureFromLamp = selectColorTemperature(this.state) ?? 'standard';
						ctt = selectColorTemperature(this.state) ?? 'standard';
					} else {
						ctt = this.layers[layer].ctt;
					}
				}
				const inten = keyframe ? (keyframe[2] * 100) : 0;
				displayIntensity = Math.max(inten, displayIntensity);
				displayTemperature = inten > 0 ? ctt : displayTemperature;
			});
		}
		return displayTemperature;
	}

	getIntensity(intensity) {
		if (intensity === 0) {
			return '---';
		} else if (intensity < 33) {
			return msg('low');
		} else if (intensity < 66) {
			return msg('medium');
		} else {
			return msg('high');
		}
	}

	getTemperature(temperature) {
		if (temperature === 0 || temperature === '0') {
			return '---';
		} else if (temperature === 'red') {
			return msg('red');
		} else if (temperature <= 2400 || temperature <= '2400K') {
			return msg('warm');
		} else if (temperature <= 2900 || temperature <= '2900K') {
			return msg('normal');
		} else {
			return msg('cold');
		}
	}

	render() {
		if (!this.lightProfile.simulationResult) return html``;
		// SUNCALC for sunrise/sunset based on fixed date chosen and location chosen in the configurator
		const startOfYear = new Date(this.fixedDate.getFullYear(), 0, 1);
		const millisecondsInDay = 24 * 60 * 60 * 1000;
		const dayOfYear = Math.floor((this.fixedDate - startOfYear) / millisecondsInDay) + 1;
		const latitude = this.location.latitude;
		const longitude = this.location.longitude;

		// const date = new Date(2024, 0, this.selectedDay, 12, 0, this.selectedSecond);
		const suntime = SunCalc.getTimes(this.fixedDate, latitude, longitude);
		const sunriseTs = Date.parse(suntime.sunrise.toLocaleString('en-US', {
			dateStyle: 'medium',
			timeStyle: 'medium',
			timeZone: this.location.timezoneName,
			hours12: false
		}));
		const sunsetTs = Date.parse(suntime.sunset.toLocaleString('en-US', {
			dateStyle: 'medium',
			timeStyle: 'medium',
			timeZone: this.location.timezoneName,
			hours12: false
		}));
		const sunrise = new Date(sunriseTs).toLocaleTimeString(this.lang, {hours12: false}).toUpperCase();
		const sunset = new Date(sunsetTs).toLocaleTimeString(this.lang, {hours12: false}).toUpperCase();

		//const fixedSunriseTime = sunrise.toLocaleTimeString(this.lang, {hour: '2-digit', minute: '2-digit'});
		//const fixedSunsetTime = sunset.toLocaleTimeString(this.lang, {hour: '2-digit', minute: '2-digit'});
		const lightDistribution = pimText(this.lang, 'LE_LightDistribution_1', 'FV.S', this.spcConfiguration, this.state.productData.translations);
		const lightDistributionParts = lightDistribution.split(' + ');

		return html`
			<table class="paramTable">
				<tbody>
				<tr>
					<th>
						${this.fixedDate.toLocaleDateString(this.lang, {
							month: 'long',
							day: 'numeric',
							hours12: false
						})}
					</th>
					<th>${msg('Time of the day')}</th>
					<th>${msg('Light Distribution')}</th>
					<th>${msg('Light intensity')}</th>
					<th>${msg('Light color')}</th>
				</tr>
				<!-- renderChannels depends on the profile chosen, there can be 2 or 1 channel, each can contain 1 or 2 layers
				each table column can have either one or two values - shift method is separating the first channel from the rest -->
				${this.rows.map((row, i) => html`
						<tr>
							${when(i === 0, () => html`
								<td rowSpan="${this.rows.length + 1}">
									<span class="sun">
										${msg('Sunrise')}: ${sunrise}<br>
										${msg('Sunset')}: ${sunset}<br>
									</span>
								</td>
							`)}
							<td class="time">
								${row.time}
							</td>
							<td class="value">
								${repeat([...this.renderChannels],
									(renderChannel) => renderChannel.id,
									(renderChannel, index) => html`
										<div>${lightDistributionParts[index] ?? renderChannel.label}</div>`)
								}
							</td>
							<td class="value">
								${repeat([...this.renderChannels],
									(renderChannel) => renderChannel.id,
									(renderChannel, index) => html`${this.getIntensity(this.getIntensityValue(renderChannel, dayOfYear, this.getTimeInSeconds(row.time)))}
									<br>`)
								}
							</td>
							<td class="value">
								${repeat([...this.renderChannels],
									(renderChannel) => renderChannel.id,
									(renderChannel, index) => html`${this.getTemperatureValue(renderChannel, dayOfYear, this.getTimeInSeconds(row.time))}
									<br>`)
								}
							</td>
						</tr>
					`
				)}
				</tbody>
			</table>
        `;
	}
}
